<!--
	@name app-firm-tab-view-fact-find-templates
	@description Firm Trading Styles
	@date 2020/08/26
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name">
		<common-structure-section thick-header>
			<template #header>
				<v-pagination v-model="currentPage" :length="pageCount" :total-visible="8" color="info" />
				<v-spacer />
				<v-btn class="mb-0" @click="openTemplatesDialog">
					<v-icon left>
						mdi-file-upload
					</v-icon>
					Add Master Templates
				</v-btn>
			</template>
			<template #body>
				<v-skeleton-loader v-if="!templates" type="list-item-avatar-three-line@3" class="px-0" />
				<div v-else-if="!templates.length">
					<div class="text-body-1 font-weight-bold mb-3">
						Upload master Fact Find templates to your firm accounts
					</div>
					<div class="text-body-2">
						Click the ADD MASTER TEMPLATES button to get started.
					</div>
				</div>
				<v-data-table
					v-else
					class="data-table"
					:footer-props="footerProps"
					:headers="headers"
					:items="templates"
					:items-per-page="itemsPerPage"
					:page.sync="currentPage"
					@page-count="pageCount = $event"
				>
					<template #body="{ items }">
						<tbody>
							<tr v-for="item in items" :key="item.id" :class="{ 'grey lighten-4 tbl-row--deleted': item.deleted }">
								<td>
									<p class="text-body-1 font-weight-bold mb-0">
										{{ item.reference }}
									</p>
									<p class="text-subtitle-2 ma-0">
										{{ item.description }}
									</p>
									<div class="d-block mt-0">
										<v-chip v-if="item.data.mandatory" class="mr-1 mt-1" color="primary" label x-small active dark>
											Mandatory
										</v-chip>
										<v-chip v-if="item.data.default" class="mr-1 mt-1" color="success" label x-small active dark>
											Default
										</v-chip>
									</div>
								</td>
								<td class="shrunk text-right">
									<div class="d-flex flex-no-wrap justify-end">
										<v-menu offset-y open-on-hover transition="slide-y-transition" left>
											<template #activator="{ on }">
												<v-btn small color="secondary" class="mr-2" v-on="on">
													<v-icon>mdi-dots-horizontal</v-icon>
												</v-btn>
											</template>
											<v-list dense class="py-0">
												<template #default>
													<v-list-item v-if="item.data.mandatory" @click="removeMandatory(item)">
														<v-list-item-icon>
															<v-icon color="primary">
																mdi-lock-open
															</v-icon>
														</v-list-item-icon>
														<v-list-item-title>Remove Mandatory</v-list-item-title>
													</v-list-item>
													<v-list-item v-else :disabled="checkMandatory()" @click="makeMandatory(item)">
														<v-list-item-icon>
															<v-icon color="primary">
																mdi-lock
															</v-icon>
														</v-list-item-icon>
														<v-list-item-title>Make Mandatory</v-list-item-title>
													</v-list-item>
													<v-list-item v-if="item.data.default" @click="removeDefault(item)">
														<v-list-item-icon>
															<v-icon color="success">
																mdi-star
															</v-icon>
														</v-list-item-icon>
														<v-list-item-title>Remove Default</v-list-item-title>
													</v-list-item>
													<v-list-item v-else :disabled="checkDefault() || checkMandatory()" @click="makeDefault(item)">
														<v-list-item-icon>
															<v-icon color="success">
																mdi-star-outline
															</v-icon>
														</v-list-item-icon>
														<v-list-item-title>
															Make Default
														</v-list-item-title>
													</v-list-item>
												</template>
											</v-list>
										</v-menu>

										<v-tooltip bottom content-class="text-center" max-width="250">
											<template #activator="{ on }">
												<v-btn color="error" small text @click="openDeleteDialog(item)" v-on="on">
													<v-icon>mdi-trash-can</v-icon>
												</v-btn>
											</template>
											<span> Delete this template </span>
										</v-tooltip>
									</div>
									<div class="d-flex flex-no-wrap justify-end mt-3 text-caption grey--text text--darken-1">
										Created: {{ item.created | moment('ddd, MMM Do YYYY') }}
									</div>
								</td>
							</tr>
						</tbody>
					</template>
				</v-data-table>
			</template>
		</common-structure-section>
		<common-dialog ref="addTemplatesDialog" :max-width="650">
			<template #header>
				Add Master Templates to your firm
			</template>
			<template #body>
				<p class="text-body-1 mb-4 font-weight-medium">
					Please select one or more files to add master templates to your firm.
				</p>
				<v-file-input
					id="fileUpload"
					ref="fileUpload"
					label="Select Files"
					prepend-icon=""
					prepend-inner-icon="mdi-attachment"
					accept=".json"
					multiple
					class="pt-0 mt-0 file-upload-imput"
					hide-details
					outlined
					chips
					@change="handleFileUpload"
				/>
				<div class="d-flex justify-center">
					<v-btn :loading="loadingSubmit" color="primary" class="mt-4" @click="addTemplates">
						Add Master Templates
					</v-btn>
				</div>
			</template>
		</common-dialog>
		<common-dialog-confirm ref="confirm" />
	</div>
</template>

<script>
	import { mapActions, mapState } from 'vuex';
	import { ElementTools } from '@/utils';
	import CommonStructureSection from '@/component/common/structure/section';
	import CommonDialog from '@/component/common/dialog';
	import CommonDialogConfirm from '@/component/common/dialog/confirm';

	export default {
		name: 'app-firm-fact-find-templates',

		components: { CommonStructureSection, CommonDialog, CommonDialogConfirm },

		props: {},

		data() {
			return {
				loadingSubmit: false,
				formData: [],
				currentPage: 1,
				itemsPerPage: 10,
				pageCount: 0,
				headers: [
					{ text: 'Template Name', align: 'left', value: 'reference', class: 'text-no-wrap' },
					{ text: '', align: 'left', value: '', sortable: false }
				],
				footerProps: {
					'items-per-page-options': [5, 10, 15, 20, 25],
					showFirstLastPage: true,
					firstIcon: 'mdi-arrow-collapse-left',
					lastIcon: 'mdi-arrow-collapse-right',
					prevIcon: 'mdi-minus',
					nextIcon: 'mdi-plus'
				}
			};
		},

		computed: {
			...mapState('FactFindTemplate', ['templates'])
		},

		created() {
			this.fetchTemplateList();
		},

		methods: {
			...mapActions('FactFindTemplate', ['fetchTemplateList', 'createTemplate', 'modifyTemplate', 'deleteTemplate']),

			/**
			 * @name openDeleteDialog
			 * @description delete a fact find template
			 */
			openDeleteDialog(template) {
				this.$refs.confirm.open('Delete Master Template', `Are you sure you wish to delete ${template.reference}?`).then(async() => {
					await this.deleteTemplate(template.id);
					await this.fetchTemplateList();
				});
			},

			/**
			 * @name openTemplatesDialog
			 * @description open the dialog to add master templates
			 */
			openTemplatesDialog() {
				this.$refs.addTemplatesDialog.open();
			},

			/**
			 * @name handleFileUpload
			 * @description handle the file upload
			 */
			handleFileUpload(files) {
				this.formData = [];
				for (let i = 0; i < files.length; i++) {
					const file = files[i];
					const reader = new FileReader();
					reader.onload = (e) => {
						try {
							const json = JSON.parse(e.target.result);
							// A valid export carries the questions either as `value` ({ fields, entities },
							// new WriteRoute format) or as `payload` (legacy AirCapture, migrated by the API).
							// A file with neither would create a master template that LOOKS right — the
							// reference and description still come through — but holds no extra questions,
							// so reject it here
							if (!json.reference || (!json.value && !json.payload)) {
								this.formData = [];
								ElementTools.fireNotification(this.$el, 'error', `${file.name} is not a valid Fact Find template export`);
								return;
							}

							this.formData.push(json);
						} catch (error) {
							this.formData = [];
							console.error('Error parsing JSON file:', error);
							ElementTools.fireNotification(this.$el, 'error', `${file.name} is not valid JSON`);
						}
					};
					reader.readAsText(file);
				}
			},

			/**
			 * @name addTemplates
			 * @description add the templates
			 */
			async addTemplates() {
				if (!this.formData.length) return ElementTools.fireNotification(this.$el, 'error', 'Select one or more valid template files to upload');

				this.loadingSubmit = true;
				const promises = this.formData.map((data) => {
					const requestData = {
						process: { reference: data.reference, description: data.description ? data.description: 'Master Template' },
						data: { master: true, mandatory: false, default: false },
						// A WriteRoute export carries `value` ({ fields, entities }); a legacy AirCapture
						// export carries `payload`, which the API migrates server-side. Send whichever the
						// file actually has — the same test WriteRoute's own importer uses — because the API
						// only migrates a body with `payload` and no `value`, and a body with neither
						// silently creates an empty template (losing every extra question).
						...(data.value ? { value: data.value } : { payload: data.payload })
					};
					return this.createTemplate(requestData);
				});

				await Promise.all(promises);
				await this.fetchTemplateList();

				this.$nextTick(() => {
					this.$refs.fileUpload.reset();
					this.formData = [];
				});
				this.loadingSubmit = false;
				this.$refs.addTemplatesDialog.close();
			},

			/**
			 * @name makeMandatory
			 * @description make a template mandatory
			 */
			async makeMandatory(template) {
				await this.modifyTemplate({ id: template.id, payload: { data: { mandatory: true } } });
				await this.fetchTemplateList();
			},

			/**
			 * @name removeMandatory
			 * @description remove the mandatory status from a template
			 */
			async removeMandatory(template) {
				await this.modifyTemplate({ id: template.id, payload: { data: { mandatory: false } } });
				await this.fetchTemplateList();
			},

			/**
			 * @name makeDefault
			 * @description make a template the default
			 */
			async makeDefault(template) {
				await this.modifyTemplate({ id: template.id, payload: { data: { default: true } } });
				await this.fetchTemplateList();
			},

			/**
			 * @name removeDefault
			 * @description remove the default status from a template
			 */
			async removeDefault(template) {
				await this.modifyTemplate({ id: template.id, payload: { data: { default: false } } });
				await this.fetchTemplateList();
			},

			/**
			 * @name checkMandatory
			 * @description check if any template is mandatory
			 */
			checkMandatory() {
				return this.templates.some((template) => template.data.mandatory);
			},

			/**
			 * @name checkDefault
			 * @description check if any template is the default
			 */
			checkDefault() {
				return this.templates.some((template) => template.data.default);
			}
		}
	};
</script>

<style lang="scss" scoped>
	.shrunk {
		width: 1px;
		white-space: nowrap;
	}
</style>
